import classNames from "classnames";
import dayjs from "dayjs";
import { useState } from "react";
import { AscAvatar, AscButton } from "../../components";
import AscCheckBox from "../../components/AscInputFields/AscCheckBox/AscCheckBox";
import AscHeading from "../../components/AscTypography/AscHeading/AscHeading";
import AscParagraph from "../../components/AscTypography/AscParagraph/AscParagraph";
import AscAuthFlowLayout from "../AscAuthFlowLayout/AscAuthFlowLayout";
import AscUpdateUserModal from "../../components/AscUpdateUserModal/AscUpdateUserModal";
import styles from "./AscWelcomeBackView.module.scss";
import AuthSideImage from "../../assets/images/auth-side-img.webp";
import { AscImageSource, ICountry, UserProfile } from "@ascendnow/shared";
import React from "react";

interface AscRegisterUserInfo {
  avatarUrl?: AscImageSource;
  name?: string;
  email?: string;
  countryOfResidence?: string;
  dateOfBirth?: string;
  school?: string;
}

export interface AscWelcomeBackViewProps {
  className?: string;
  userInfo: AscRegisterUserInfo;
  correctLoading?: boolean;
  updateProfileLoading?: boolean;
  onUpdateUserInfo: (userProfile: UserProfile) => Promise<void>;
  onCorrect: (data: {
    userInfo: AscRegisterUserInfo;
    rememberMe: boolean;
  }) => void;
  countriesList?: ICountry[];
}

export default function AscWelcomeBackView({
  className,
  userInfo,
  onUpdateUserInfo,
  onCorrect,
  correctLoading,
  updateProfileLoading,
  countriesList,
}: AscWelcomeBackViewProps) {
  const [rememberMe, setRememberMe] = useState(true);
  const [showUpdateProfileModal, setShowUpdateProfileModal] = useState(false);

  async function handleUpdateProfile(userProfile: UserProfile) {
    await onUpdateUserInfo?.(userProfile);
    closeUpdateProfileModal();
  }

  function handleRememberMeChange(e: React.ChangeEvent<HTMLInputElement>) {
    setRememberMe(e?.target?.checked);
  }

  function handleCorrectClick() {
    onCorrect?.({ userInfo, rememberMe });
  }
  function openUpdateProfileModal() {
    setShowUpdateProfileModal(true);
  }

  function closeUpdateProfileModal() {
    setShowUpdateProfileModal(false);
  }

  return (
    <AscAuthFlowLayout sideImageUrl={AuthSideImage}>
      <div
        data-testid="asc-welcome-back-view"
        className={classNames(styles.ground, className)}
      >
        <header className={styles.heading}>
          <AscHeading className={styles.title} level={1}>
            Welcome back
          </AscHeading>
          <AscParagraph className={styles.subTitle}>
            Let's make sure we have the right information
          </AscParagraph>
        </header>

        <div className={styles.userInfoContent}>
          <div className={styles.infoBox}>
            <AscAvatar className={styles.avatar} src={userInfo?.avatarUrl} />
            <div className={styles.textGroup}>
              <div className={styles.label}>Name</div>
              <div className={styles.value}>{userInfo?.name}</div>
            </div>
            <div className={styles.textGroup}>
              <div className={styles.label}>Email</div>
              <div className={styles.value}>{userInfo?.email}</div>
            </div>
            <div className={styles.textGroup}>
              <div className={styles.label}>Country of Residence</div>
              <div className={styles.value}>
                {userInfo?.countryOfResidence || "-"}
              </div>
            </div>
            <div className={styles.textGroup}>
              <div className={styles.label}>Date of Birth</div>
              <div className={styles.value}>
                {dayjs(userInfo?.dateOfBirth).format("D MMMM[,] YYYY")}
              </div>
            </div>
            <div className={styles.textGroup}>
              <div className={styles.label}>School</div>
              <div className={styles.value}>{userInfo?.school}</div>
            </div>
          </div>
          <div className={styles.rememberMeContainer}>
            <AscCheckBox
              checked={rememberMe}
              onChange={handleRememberMeChange}
            />
            <label className={styles.rememberMeText}>Remember me</label>
          </div>
        </div>

        <footer className={styles.footer}>
          <AscButton
            className={styles.footerButton}
            variant="secondary"
            size="large"
            onClick={openUpdateProfileModal}
            loading={updateProfileLoading}
          >
            Edit
          </AscButton>
          <AscButton
            className={styles.footerButton}
            variant="primary"
            size="large"
            onClick={handleCorrectClick}
            loading={correctLoading}
            disabled={updateProfileLoading}
          >
            Correct
          </AscButton>
        </footer>
      </div>
      <AscUpdateUserModal
        visible={showUpdateProfileModal}
        setVisible={setShowUpdateProfileModal}
        onSave={handleUpdateProfile}
        initialProfileValues={userInfo}
        onCancel={closeUpdateProfileModal}
        loading={updateProfileLoading}
        countriesList={countriesList}
      />
    </AscAuthFlowLayout>
  );
}
